import {useQueryClient} from '@github-ui/react-query'
import {useCallback, useMemo} from 'react'
import {useDebounce} from '@github-ui/use-debounce'
import type {APITaskGroup} from '../types/api-task'
import type {IdToPullRequestMap} from '../types/pull'
import type {TaskType, UnhydratedTask} from '../types/task'
import type {EnvironmentKind} from '@github-ui/agent-sessions-types/environment'
import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {transformAPITasksToUnhydrated} from '../utils/task-helpers'
import {groupedTasksApiPathWithParams, type TasksQueryParams} from '../utils/api-url-helpers'
import {getInteractiveRefetchInterval, hasInteractiveTasks} from '../agents/interactive-polling'
import {useCAPIQuery} from '@github-ui/copilot-shared-ui/capi/use-capi-query'
import {usePullRequests} from './usePullRequests'
import {useAgentSessionsAlive} from './use-agent-sessions-alive'
import {NO_REPOSITORY_GROUP} from '../constants/grouping'
import type {TaskRepoGroup} from './use-grouped-tasks'
import type {TaskFilter} from './useTasks'
import {useHiddenEarlyTaskIds} from '../ahp/early-task-visibility'

export const agentGroupedTasksQueryKey = ['agent-grouped-tasks']

/** Default cap on the number of repo groups the sidebar requests. */
export const DEFAULT_MAX_GROUPS = 10

/** A group after transformation but before PR hydration. */
type UnhydratedGroup = {
  nwo: string | undefined
  repoId: number | undefined
  /** Structured repository short name from `APIGroupRepository.name`, when the group is repo-backed. */
  repositoryName: string | undefined
  tasks: UnhydratedTask[]
}

type FetchGroupedTasksResponse = {
  groups: UnhydratedGroup[]
}

function taskFilterToArchived(taskFilter: TaskFilter): boolean | undefined {
  if (taskFilter === 'all') return undefined
  return taskFilter === 'completed'
}

function groupNwo(group: APITaskGroup): string | undefined {
  const repo = group.repository
  if (!repo) return undefined
  return repo.full_name || `${repo.owner.login}/${repo.name}`
}

/** Attaches PR data and the group's nwo/repo_id to a task. */
function hydrateTask(task: UnhydratedTask, group: UnhydratedGroup, prMap: IdToPullRequestMap) {
  const pull = task.resource_id ? prMap[task.resource_id] : undefined
  return {
    ...task,
    pull,
    repo_id: group.repoId ?? task.repo_id,
    nwo: pull?.repository_nwo ?? group.nwo,
    repositoryName: group.repositoryName ?? task.repositoryName,
  }
}

/** Moves the repo-less group to the end to match the client-side grouping order. */
function withOrphanGroupLast(groups: TaskRepoGroup[]): TaskRepoGroup[] {
  const orphan = groups.find(([key]) => key === NO_REPOSITORY_GROUP)
  return orphan ? [...groups.filter(group => group !== orphan), orphan] : groups
}

export async function fetchGroupedTasks({
  queryParams,
}: {
  queryParams?: TasksQueryParams
}): Promise<FetchGroupedTasksResponse> {
  const path = groupedTasksApiPathWithParams(queryParams)
  const response = await fetchCAPI({path})
  const data = await response.json()

  const apiGroups = (data.groups ?? []) as APITaskGroup[]
  return {groups: transformApiGroupsToUnhydrated(apiGroups)}
}

/** Transforms raw grouped-tasks API groups into the pre-hydration group shape. */
export function transformApiGroupsToUnhydrated(apiGroups: APITaskGroup[]): UnhydratedGroup[] {
  return apiGroups.map<UnhydratedGroup>(group => ({
    nwo: groupNwo(group),
    repoId: group.repository?.id,
    repositoryName: group.repository?.name,
    tasks: transformAPITasksToUnhydrated(group.tasks),
  }))
}

export type UseGroupedTasksDataParams = {
  taskFilter?: TaskFilter
  taskType?: TaskType
  customAgentName?: string
  creatorIds?: number[]
  statuses?: string[]
  sort?: string
  searchQuery?: string
  agentIds?: number[]
  facetMode?: 'and' | 'legacy_or'
  includeTypeAgentIds?: readonly number[]
  includeTypeEnvironmentKinds?: readonly EnvironmentKind[]
  repoId?: number
  maxGroups?: number
  sessionsPollingInterval?: number | false
  enabled?: boolean
  scope?: string
  /**
   * Raw grouped-tasks API groups from the server (SSR hydration). When present,
   * seeds the query's initialData so the sidebar renders without a client fetch.
   */
  initialGroups?: APITaskGroup[]
}

/**
 * Data hook for the server-grouped `/agents/grouped-tasks` endpoint. Returns tasks
 * grouped by repository, hydrating PR data via the pull-requests endpoint.
 */
export function useGroupedTasksData({
  taskFilter = 'active',
  taskType,
  customAgentName,
  creatorIds,
  statuses,
  sort,
  searchQuery,
  agentIds,
  facetMode,
  includeTypeAgentIds,
  includeTypeEnvironmentKinds,
  repoId,
  maxGroups = DEFAULT_MAX_GROUPS,
  sessionsPollingInterval = 3000,
  enabled = true,
  scope,
  initialGroups,
}: UseGroupedTasksDataParams = {}) {
  const queryClient = useQueryClient()

  const handleAliveUpdate = useCallback(() => {
    queryClient.invalidateQueries({queryKey: agentGroupedTasksQueryKey})
    queryClient.invalidateQueries({queryKey: ['agent-sessions-pull-requests']})
  }, [queryClient])

  const shouldSubscribeToAlive = sessionsPollingInterval !== false
  const debouncedHandleAliveUpdate = useDebounce(
    handleAliveUpdate,
    typeof sessionsPollingInterval === 'number' ? sessionsPollingInterval : 3000,
    {maxWait: 10000},
  )

  useAgentSessionsAlive({
    onEvent: shouldSubscribeToAlive ? debouncedHandleAliveUpdate : () => {},
    enableCreateEvents: false,
    enableUpdateEvents: shouldSubscribeToAlive,
    enableLogUpdates: false,
    enableTaskUpdates: shouldSubscribeToAlive,
    enabled: shouldSubscribeToAlive && enabled,
  })

  const queryParams: TasksQueryParams = {
    archived: taskFilterToArchived(taskFilter),
    type: taskType,
    customAgentName,
    creatorIds,
    statuses,
    sort,
    query: searchQuery,
    agentIds,
    facetMode,
    includeTypeAgentIds,
    includeTypeEnvironmentKinds,
    repositoryId: repoId,
    maxGroups,
  }

  const queryKey = [
    ...agentGroupedTasksQueryKey,
    taskFilter,
    {
      taskType,
      customAgentName,
      creatorIds,
      statuses,
      sort,
      query: searchQuery,
      agentIds,
      facetMode,
      includeTypeAgentIds,
      includeTypeEnvironmentKinds,
      repoId,
      maxGroups,
    },
    ...(scope === undefined ? [] : [scope]),
  ]

  // Seed from SSR groups on first render so the sidebar renders immediately; the
  // query still refetches in the background to pick up any newer server state.
  const initialData = useMemo<FetchGroupedTasksResponse | undefined>(
    () => (initialGroups ? {groups: transformApiGroupsToUnhydrated(initialGroups)} : undefined),
    [initialGroups],
  )

  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const query = useCAPIQuery<FetchGroupedTasksResponse>({
    queryKey,
    queryFn: async () => await fetchGroupedTasks({queryParams}),
    initialData,
    refetchInterval: ({state: {data}}) =>
      getInteractiveRefetchInterval(hasInteractiveTasks(data?.groups.flatMap(g => g.tasks) ?? [])),
    refetchOnWindowFocus: true,
    enabled,
  })

  const hiddenTaskIds = useHiddenEarlyTaskIds()
  const unhydratedGroups = useMemo(
    () =>
      query.data?.groups.flatMap(group => {
        const tasks = group.tasks.filter(task => !task.id || !hiddenTaskIds.has(task.id))
        if (tasks.length === group.tasks.length) return [group]
        return tasks.length ? [{...group, tasks}] : []
      }) ?? [],
    [query.data, hiddenTaskIds],
  )

  const allTasks = useMemo(() => unhydratedGroups.flatMap(group => group.tasks), [unhydratedGroups])

  // Only tasks backed by a PR artifact should be hydrated; mixing in undefined
  // resource_ids serializes to a malformed `pull_request_ids=,2` request.
  const tasksWithResourceIds = useMemo(
    () => allTasks.filter(task => task.resource_id !== undefined && task.resource_id !== -1),
    [allTasks],
  )

  const {data: idToPullRequestMap, isError: isErrorPullRequests} = usePullRequests({tasks: tasksWithResourceIds})

  const groups = useMemo<TaskRepoGroup[]>(() => {
    const mapped = unhydratedGroups.map(
      group =>
        [
          group.nwo ?? NO_REPOSITORY_GROUP,
          group.tasks.map(task => hydrateTask(task, group, idToPullRequestMap)),
        ] as const,
    )
    return withOrphanGroupLast(mapped)
  }, [unhydratedGroups, idToPullRequestMap])

  const isLoading = query.isLoading && !query.data
  const isError = query.isError || isErrorPullRequests

  return {groups, isLoading, isError, query}
}
